// ============================================================
// PythonQuiz.js
//
// Gamified Python Mastery Quiz — ProDev Platform
//
// This file is adapted from JavaQuiz.js — same engine,
// completely new Python question bank covering:
//   Syntax & Basics, Variables & Data Types, Control Flow,
//   Functions, OOP, Modules & Packages, File Handling,
//   Exception Handling, List Comprehensions, Decorators,
//   Generators, Lambda & Functional, Multithreading
//
// Features (identical to JavaQuiz):
//   - Personalised welcome screen (greets user by name)
//   - Rules & conduct screen with time limits explained
//   - Web Speech API — audible countdown before quiz starts
//   - Dynamic difficulty: re-arranges easy → hard on retry
//     based on initial performance per category
//   - Star reward system:
//       ★★★ = 100%  → Golden Buzzer + Certification Exam unlocked
//       ★★  = 90%+  → Certification Exam unlocked
//       ★   = 80%+  → Certification Exam unlocked
//       ✗   = <80%  → must retake (dynamically reordered)
//   - Anti-cheat engine:
//       • Tab/window visibility detection
//       • Browser focus loss detection
//       • Right-click disabled during quiz
//       • Copy/paste keyboard shortcuts blocked
//       • DevTools size-change detection
//       • Violations logged and shown on results screen
//   - Sound synthesis (Web Audio API — no external audio files):
//       • Correct answer chime
//       • Wrong answer buzz
//       • Timer warning beep (last 60 seconds)
//       • Quiz complete fanfare
//       • Golden buzzer sound (100% score)
//   - Category-level performance breakdown
//   - Leaderboard (localStorage — separate from Java quiz)
//   - Brand colours: #100720 dark, #E5C95F gold
//
// Junior dev notes are scattered throughout explaining every
// major concept, hook, and pattern used in this file.
// ============================================================

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Box, Button, Card, CardContent, Typography,
    LinearProgress, Chip, Divider,
    styled, keyframes,
} from '@mui/material';
import { Link } from 'react-router-dom';

// ── MUI Icons ─────────────────────────────────────────────────
import StarIcon               from '@mui/icons-material/Star';
import StarBorderIcon         from '@mui/icons-material/StarBorder';
import EmojiEventsIcon        from '@mui/icons-material/EmojiEvents';
import WarningAmberIcon       from '@mui/icons-material/WarningAmber';
import CheckCircleIcon        from '@mui/icons-material/CheckCircle';
import CancelIcon             from '@mui/icons-material/Cancel';
import TimerIcon              from '@mui/icons-material/Timer';
import SchoolIcon             from '@mui/icons-material/School';
import SecurityIcon           from '@mui/icons-material/Security';
import PlayArrowIcon          from '@mui/icons-material/PlayArrow';
import ReplayIcon             from '@mui/icons-material/Replay';
import ArrowForwardIcon       from '@mui/icons-material/ArrowForward';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import BusinessCenterIcon     from '@mui/icons-material/BusinessCenter';

// ============================================================
//  BRAND CONSTANTS
//  Same palette as Header, Vision, Mission, JavaQuiz.
//  Change once here — updates everywhere.
// ============================================================
const BRAND = {
    dark:       '#100720',
    darkCard:   '#1a0f35',
    darkBorder: 'rgba(229,201,95,0.2)',
    gold:       '#E5C95F',
    goldenrod:  '#DAA520',
    goldFaint:  'rgba(229,201,95,0.08)',
    white:      '#FFFFFF',
    dimWhite:   'rgba(255,255,255,0.65)',
    success:    '#3ECF8E',
    error:      '#FF6B6B',
    warning:    '#FF9F43',
};

// ============================================================
//  QUIZ CONFIGURATION
//  All quiz behaviour tuning lives here — no hunting through code.
//  leaderboardKey is different from JavaQuiz so scores don't mix.
// ============================================================
const QUIZ_CONFIG = {
    totalTimeSeconds: 1200,   // 20 minutes
    warningThreshold: 60,     // Seconds left when timer turns red
    passMark:         0.80,   // 80% = 1 star minimum
    twoStarMark:      0.90,   // 90% = 2 stars
    threeStarMark:    1.00,   // 100% = 3 stars + Golden Buzzer
    maxViolations:    3,      // Auto-terminate after 3 violations
    leaderboardKey:   'python_quiz_leaderboard', // Separate from Java
};

// ============================================================
//  PYTHON QUESTION BANK
//
//  13 categories × 3 questions = 39 questions total.
//  difficulty: 'easy' | 'medium' | 'hard'
//  Used by reorderByDifficulty() on retry attempts.
//
//  Junior dev note: All data is defined as a plain JS array
//  of objects. We keep data separate from UI — easy to extend,
//  easy to test, easy to hand off to a backend API later.
// ============================================================
const pythonQuestionBank = [

    // ── Syntax & Basics ───────────────────────────────────────
    {
        question: 'Which symbol is used to write a single-line comment in Python?',
        options: ['// ', '/* */', '#', '--'],
        answer: '#',
        explanation: 'Python uses `#` for single-line comments. There are no block comment symbols.',
        link: '/python-syntax',
        category: 'Syntax & Basics',
        difficulty: 'easy',
    },
    {
        question: 'What is the correct file extension for a Python file?',
        options: ['.py', '.python', '.pt', '.pyt'],
        answer: '.py',
        explanation: 'Python files use the `.py` extension.',
        link: '/python-syntax',
        category: 'Syntax & Basics',
        difficulty: 'easy',
    },
    {
        question: 'Which function is used to display output to the console in Python?',
        options: ['echo()', 'console.log()', 'print()', 'display()'],
        answer: 'print()',
        explanation: '`print()` is the built-in function for console output in Python.',
        link: '/python-syntax',
        category: 'Syntax & Basics',
        difficulty: 'easy',
    },

    // ── Variables & Data Types ────────────────────────────────
    {
        question: 'Which of the following is an immutable data type in Python?',
        options: ['list', 'dict', 'set', 'tuple'],
        answer: 'tuple',
        explanation: 'Tuples are immutable — their contents cannot be changed after creation.',
        link: '/python-datatypes',
        category: 'Variables & Data Types',
        difficulty: 'medium',
    },
    {
        question: 'What is the output of type(3.14) in Python?',
        options: ["<class 'int'>", "<class 'float'>", "<class 'double'>", "<class 'decimal'>"],
        answer: "<class 'float'>",
        explanation: '3.14 is a floating-point number. Python represents it as `float`.',
        link: '/python-datatypes',
        category: 'Variables & Data Types',
        difficulty: 'easy',
    },
    {
        question: 'How do you declare a variable in Python?',
        options: ['var x = 5', 'int x = 5', 'x = 5', 'let x = 5'],
        answer: 'x = 5',
        explanation: 'Python uses dynamic typing — no keyword needed. Just assign with `=`.',
        link: '/python-variables',
        category: 'Variables & Data Types',
        difficulty: 'easy',
    },

    // ── Control Flow ──────────────────────────────────────────
    {
        question: 'Which keyword starts a conditional block in Python?',
        options: ['if', 'when', 'check', 'cond'],
        answer: 'if',
        explanation: '`if` is the standard keyword for conditional blocks in Python.',
        link: '/python-control-flow',
        category: 'Control Flow',
        difficulty: 'easy',
    },
    {
        question: 'What does the `elif` keyword mean in Python?',
        options: ['End loop if', 'Else if', 'Evaluate logic', 'Exit loop if false'],
        answer: 'Else if',
        explanation: '`elif` is Python\'s shorthand for "else if" — used for multiple conditions.',
        link: '/python-control-flow',
        category: 'Control Flow',
        difficulty: 'easy',
    },
    {
        question: 'Which loop is used to iterate over a sequence in Python?',
        options: ['while', 'for', 'loop', 'foreach'],
        answer: 'for',
        explanation: '`for` loops iterate over sequences like lists, tuples, strings, and ranges.',
        link: '/python-control-flow',
        category: 'Control Flow',
        difficulty: 'easy',
    },

    // ── Functions ─────────────────────────────────────────────
    {
        question: 'Which keyword is used to define a function in Python?',
        options: ['func', 'function', 'def', 'define'],
        answer: 'def',
        explanation: '`def` is used to define a function in Python.',
        link: '/python-functions',
        category: 'Functions',
        difficulty: 'easy',
    },
    {
        question: 'What does *args allow in a Python function?',
        options: [
            'Pass keyword arguments',
            'Pass a variable number of positional arguments',
            'Unpack a dictionary',
            'Define default parameters',
        ],
        answer: 'Pass a variable number of positional arguments',
        explanation: '`*args` collects extra positional arguments into a tuple inside the function.',
        link: '/python-functions',
        category: 'Functions',
        difficulty: 'medium',
    },
    {
        question: 'What is a lambda function in Python?',
        options: [
            'A named function with multiple lines',
            'An anonymous single-expression function',
            'A function that returns None',
            'A recursive function',
        ],
        answer: 'An anonymous single-expression function',
        explanation: '`lambda` creates small anonymous functions: `lambda x: x * 2`.',
        link: '/python-functions',
        category: 'Functions',
        difficulty: 'medium',
    },

    // ── OOP ───────────────────────────────────────────────────
    {
        question: 'Which keyword is used to define a class in Python?',
        options: ['Class', 'class', 'object', 'type'],
        answer: 'class',
        explanation: '`class` (lowercase) defines a class in Python.',
        link: '/python-oop',
        category: 'OOP',
        difficulty: 'easy',
    },
    {
        question: 'What is the purpose of __init__ in a Python class?',
        options: [
            'It destroys the object',
            'It initialises the object\'s attributes when created',
            'It defines class methods',
            'It imports modules',
        ],
        answer: 'It initialises the object\'s attributes when created',
        explanation: '`__init__` is the constructor — called automatically when a new object is created.',
        link: '/python-oop',
        category: 'OOP',
        difficulty: 'medium',
    },
    {
        question: 'What does `self` refer to in a Python class method?',
        options: ['The class itself', 'The current instance of the class', 'The parent class', 'A static variable'],
        answer: 'The current instance of the class',
        explanation: '`self` refers to the specific object calling the method — like `this` in Java.',
        link: '/python-oop',
        category: 'OOP',
        difficulty: 'medium',
    },

    // ── Modules & Packages ────────────────────────────────────
    {
        question: 'Which keyword is used to import a module in Python?',
        options: ['include', 'require', 'import', 'use'],
        answer: 'import',
        explanation: '`import` brings external modules into the current script.',
        link: '/python-modules',
        category: 'Modules & Packages',
        difficulty: 'easy',
    },
    {
        question: 'What does `from math import sqrt` do?',
        options: [
            'Imports the entire math module',
            'Imports only the sqrt function from math',
            'Creates a new math function',
            'Renames the math module',
        ],
        answer: 'Imports only the sqrt function from math',
        explanation: '`from module import name` imports a specific name, avoiding the module prefix.',
        link: '/python-modules',
        category: 'Modules & Packages',
        difficulty: 'easy',
    },
    {
        question: 'What file makes a directory a Python package?',
        options: ['main.py', 'setup.py', '__init__.py', 'package.py'],
        answer: '__init__.py',
        explanation: 'A directory with `__init__.py` is treated as a Python package.',
        link: '/python-packages',
        category: 'Modules & Packages',
        difficulty: 'medium',
    },

    // ── File Handling ─────────────────────────────────────────
    {
        question: 'Which function is used to open a file in Python?',
        options: ['file()', 'open()', 'read()', 'load()'],
        answer: 'open()',
        explanation: '`open()` opens a file and returns a file object.',
        link: '/python-file-handling',
        category: 'File Handling',
        difficulty: 'easy',
    },
    {
        question: 'What mode opens a file for writing, creating it if it does not exist?',
        options: ["'r'", "'a'", "'w'", "'x'"],
        answer: "'w'",
        explanation: "'w' mode opens for writing. It creates the file if missing and truncates if it exists.",
        link: '/python-file-handling',
        category: 'File Handling',
        difficulty: 'medium',
    },
    {
        question: 'What is the benefit of using `with open(...) as f:`?',
        options: [
            'It reads faster',
            'It automatically closes the file when the block exits',
            'It locks the file from other users',
            'It converts the file to a string',
        ],
        answer: 'It automatically closes the file when the block exits',
        explanation: 'The `with` statement ensures the file is properly closed even if an error occurs.',
        link: '/python-file-handling',
        category: 'File Handling',
        difficulty: 'medium',
    },

    // ── Exception Handling ────────────────────────────────────
    {
        question: 'Which keyword is used to handle exceptions in Python?',
        options: ['catch', 'except', 'handle', 'error'],
        answer: 'except',
        explanation: 'Python uses `try / except` for exception handling (not `try / catch` like Java).',
        link: '/python-exceptions',
        category: 'Exception Handling',
        difficulty: 'easy',
    },
    {
        question: 'What does the `finally` block do in Python?',
        options: [
            'Catches the exception',
            'Raises an exception',
            'Runs only if no exception occurs',
            'Runs regardless of whether an exception occurred',
        ],
        answer: 'Runs regardless of whether an exception occurred',
        explanation: '`finally` always executes — used for cleanup code like closing files.',
        link: '/python-exceptions',
        category: 'Exception Handling',
        difficulty: 'medium',
    },
    {
        question: 'Which built-in exception is raised when dividing by zero?',
        options: ['ValueError', 'ArithmeticError', 'ZeroDivisionError', 'MathError'],
        answer: 'ZeroDivisionError',
        explanation: 'Python raises `ZeroDivisionError` whenever division by zero is attempted.',
        link: '/python-exceptions',
        category: 'Exception Handling',
        difficulty: 'medium',
    },

    // ── List Comprehensions ───────────────────────────────────
    {
        question: 'What is the output of [x * 2 for x in range(3)]?',
        options: ['[1, 2, 3]', '[0, 2, 4]', '[2, 4, 6]', '[0, 1, 2]'],
        answer: '[0, 2, 4]',
        explanation: 'range(3) gives 0, 1, 2. Multiplied by 2: 0, 2, 4.',
        link: '/python-comprehensions',
        category: 'List Comprehensions',
        difficulty: 'medium',
    },
    {
        question: 'Which syntax correctly filters even numbers using a list comprehension?',
        options: [
            '[x if x % 2 == 0 for x in range(10)]',
            '[x for x in range(10) if x % 2 == 0]',
            '[x for x in range(10) where x % 2 == 0]',
            '[x % 2 for x in range(10)]',
        ],
        answer: '[x for x in range(10) if x % 2 == 0]',
        explanation: 'The `if` condition goes at the end of the comprehension to filter items.',
        link: '/python-comprehensions',
        category: 'List Comprehensions',
        difficulty: 'medium',
    },
    {
        question: 'What does a dictionary comprehension look like in Python?',
        options: [
            '{x: x**2 for x in range(5)}',
            '[x: x**2 for x in range(5)]',
            '(x: x**2 for x in range(5))',
            '{x => x**2 for x in range(5)}',
        ],
        answer: '{x: x**2 for x in range(5)}',
        explanation: 'Dictionary comprehensions use `{key: value for item in iterable}` syntax.',
        link: '/python-comprehensions',
        category: 'List Comprehensions',
        difficulty: 'hard',
    },

    // ── Decorators ────────────────────────────────────────────
    {
        question: 'What symbol is used to apply a decorator to a function?',
        options: ['$', '#', '@', '&'],
        answer: '@',
        explanation: '`@decorator_name` placed above a function applies that decorator.',
        link: '/python-decorators',
        category: 'Decorators',
        difficulty: 'medium',
    },
    {
        question: 'What does a decorator do in Python?',
        options: [
            'Deletes a function',
            'Wraps a function to extend or modify its behaviour',
            'Creates a new class',
            'Imports a module',
        ],
        answer: 'Wraps a function to extend or modify its behaviour',
        explanation: 'Decorators are higher-order functions that wrap another function, adding functionality.',
        link: '/python-decorators',
        category: 'Decorators',
        difficulty: 'hard',
    },
    {
        question: 'Which built-in decorator makes a method callable on the class, not an instance?',
        options: ['@property', '@staticmethod', '@classmethod', '@abstract'],
        answer: '@staticmethod',
        explanation: '`@staticmethod` defines a method that does not receive `self` or `cls`.',
        link: '/python-decorators',
        category: 'Decorators',
        difficulty: 'hard',
    },

    // ── Generators ────────────────────────────────────────────
    {
        question: 'Which keyword turns a function into a generator in Python?',
        options: ['return', 'yield', 'generate', 'send'],
        answer: 'yield',
        explanation: '`yield` pauses the function and returns a value without ending execution.',
        link: '/python-generators',
        category: 'Generators',
        difficulty: 'medium',
    },
    {
        question: 'What is the advantage of using a generator over a list?',
        options: [
            'Generators are faster to write',
            'Generators produce values lazily, using less memory',
            'Generators support more data types',
            'Generators run in parallel',
        ],
        answer: 'Generators produce values lazily, using less memory',
        explanation: 'Generators yield one value at a time — ideal for large datasets that cannot fit in memory.',
        link: '/python-generators',
        category: 'Generators',
        difficulty: 'hard',
    },
    {
        question: 'Which function retrieves the next value from a generator?',
        options: ['get()', 'fetch()', 'next()', 'step()'],
        answer: 'next()',
        explanation: '`next(generator)` advances the generator and returns the next yielded value.',
        link: '/python-generators',
        category: 'Generators',
        difficulty: 'medium',
    },

    // ── Lambda & Functional ───────────────────────────────────
    {
        question: 'What does the map() function do in Python?',
        options: [
            'Creates a dictionary',
            'Applies a function to every item in an iterable',
            'Filters items from a list',
            'Sorts a list',
        ],
        answer: 'Applies a function to every item in an iterable',
        explanation: '`map(func, iterable)` returns a map object with the function applied to each item.',
        link: '/python-functional',
        category: 'Lambda & Functional',
        difficulty: 'medium',
    },
    {
        question: 'What does filter() return?',
        options: [
            'A sorted list',
            'Items where the function returns True',
            'Items where the function returns False',
            'A dictionary of matching items',
        ],
        answer: 'Items where the function returns True',
        explanation: '`filter(func, iterable)` keeps only items for which `func` returns True.',
        link: '/python-functional',
        category: 'Lambda & Functional',
        difficulty: 'medium',
    },
    {
        question: 'What does reduce() do and where is it imported from?',
        options: [
            'Sorts a list — from builtins',
            'Applies a function cumulatively — from functools',
            'Filters a list — from itertools',
            'Maps a function — from builtins',
        ],
        answer: 'Applies a function cumulatively — from functools',
        explanation: '`functools.reduce(func, iterable)` applies func cumulatively, reducing to a single value.',
        link: '/python-functional',
        category: 'Lambda & Functional',
        difficulty: 'hard',
    },

    // ── Multithreading ────────────────────────────────────────
    {
        question: 'Which module is used for threading in Python?',
        options: ['multiprocessing', 'threading', 'concurrent', 'asyncio'],
        answer: 'threading',
        explanation: 'The `threading` module provides Thread class and synchronisation primitives.',
        link: '/python-multithreading',
        category: 'Multithreading',
        difficulty: 'medium',
    },
    {
        question: 'Which method starts a thread in Python?',
        options: ['run()', 'begin()', 'start()', 'execute()'],
        answer: 'start()',
        explanation: '`thread.start()` begins execution in a new thread by calling `run()` internally.',
        link: '/python-multithreading',
        category: 'Multithreading',
        difficulty: 'medium',
    },
    {
        question: 'What is the GIL in Python?',
        options: [
            'Global Import Library',
            'Global Interpreter Lock — prevents true parallel thread execution',
            'General Input Loop',
            'Garbage Isolation Layer',
        ],
        answer: 'Global Interpreter Lock — prevents true parallel thread execution',
        explanation: 'The GIL ensures only one thread runs Python bytecode at a time, limiting CPU-bound parallelism.',
        link: '/python-multithreading',
        category: 'Multithreading',
        difficulty: 'hard',
    },
];

// ============================================================
//  UTILITY FUNCTIONS
//  Pure functions — identical logic to JavaQuiz, different data.
// ============================================================

/**
 * shuffle
 * Returns a randomly ordered copy — does NOT mutate the original.
 */
const shuffle = (arr) => [...arr].sort(() => Math.random() - 0.5);

/**
 * getStars
 * Maps score percentage to 0–3 stars using QUIZ_CONFIG thresholds.
 */
const getStars = (score, total) => {
    const ratio = score / total;
    if (ratio >= QUIZ_CONFIG.threeStarMark) return 3;
    if (ratio >= QUIZ_CONFIG.twoStarMark)   return 2;
    if (ratio >= QUIZ_CONFIG.passMark)       return 1;
    return 0;
};

/**
 * getBadge
 * Returns a Python-themed badge label for the results screen.
 */
const getBadge = (stars) => {
    if (stars === 3) return '🐍 Python Wizard';
    if (stars === 2) return '⚡ Python Pro';
    if (stars === 1) return '📘 Python Apprentice';
    return '🌱 Python Novice';
};

/**
 * formatTime
 * Converts seconds to MM:SS string.
 * e.g. 125 → "02:05"
 */
const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
};

/**
 * saveScore / loadScores
 * Persist top 5 scores to localStorage under the Python key.
 * Note: uses python_quiz_leaderboard — separate from Java scores.
 */
const saveScore = (entry) => {
    const prev    = JSON.parse(localStorage.getItem(QUIZ_CONFIG.leaderboardKey) || '[]');
    const updated = [entry, ...prev].slice(0, 5);
    localStorage.setItem(QUIZ_CONFIG.leaderboardKey, JSON.stringify(updated));
};

const loadScores = () =>
    JSON.parse(localStorage.getItem(QUIZ_CONFIG.leaderboardKey) || '[]');

/**
 * reorderByDifficulty
 * On retry, sorts questions so weak categories (low accuracy) come
 * last and strong categories come first.
 * This gives learners an easy warmup before hitting hard topics.
 */
const reorderByDifficulty = (questions, categoryScores) => {
    return [...questions].sort((a, b) => {
        const aRatio = categoryScores[a.category]
            ? categoryScores[a.category].correct / categoryScores[a.category].total
            : 0.5;
        const bRatio = categoryScores[b.category]
            ? categoryScores[b.category].correct / categoryScores[b.category].total
            : 0.5;
        return bRatio - aRatio; // Higher ratio = user is stronger = comes first
    });
};

// ============================================================
//  SOUND ENGINE — Web Audio API
//
//  All sounds synthesised in the browser — no audio files needed.
//  audioCtx is lazily initialised on first user interaction to
//  comply with browser autoplay policies.
// ============================================================

let audioCtx = null;

/**
 * getAudioContext
 * Creates the AudioContext on first call (lazy init).
 * window['webkitAudioContext'] is the Safari fallback.
 * We use bracket notation here because TypeScript's `as any`
 * is not valid in plain .js files.
 */
const getAudioContext = () => {
    if (!audioCtx) {
        const AudioContextClass = window.AudioContext || window['webkitAudioContext'];
        audioCtx = new AudioContextClass();
    }
    return audioCtx;
};

/**
 * playTone
 * Core audio primitive. Creates an oscillator → gain node → output.
 * Smooth fade-in/out prevents harsh audio clicks.
 */
const playTone = (frequency, duration, type = 'sine', volume = 0.3, delay = 0) => {
    try {
        const ctx  = getAudioContext();
        const osc  = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.type = type;
        osc.frequency.setValueAtTime(frequency, ctx.currentTime + delay);
        gain.gain.setValueAtTime(0, ctx.currentTime + delay);
        gain.gain.linearRampToValueAtTime(volume, ctx.currentTime + delay + 0.02);
        gain.gain.linearRampToValueAtTime(0, ctx.currentTime + delay + duration);
        osc.start(ctx.currentTime + delay);
        osc.stop(ctx.currentTime + delay + duration + 0.05);
    } catch (e) {
        console.warn('Sound playback failed:', e);
    }
};

/** Correct answer — ascending two-note chime */
const playCorrect = () => {
    playTone(523, 0.15, 'sine', 0.3, 0);
    playTone(659, 0.2,  'sine', 0.3, 0.12);
};

/** Wrong answer — descending square buzz */
const playWrong = () => {
    playTone(300, 0.1,  'square', 0.2, 0);
    playTone(200, 0.15, 'square', 0.2, 0.12);
};

/** Timer warning — short high beep (last 60 seconds) */
const playWarningBeep = () => {
    playTone(880, 0.08, 'sine', 0.25, 0);
};

/** Quiz complete — ascending 4-note fanfare */
const playComplete = () => {
    [523, 659, 784, 1047].forEach((freq, i) =>
        playTone(freq, 0.25, 'sine', 0.35, i * 0.12)
    );
};

/** Golden Buzzer — triumphant chord + rising arpeggio (100% only) */
const playGoldenBuzzer = () => {
    [523, 659, 784].forEach((f) => playTone(f, 0.6, 'sine', 0.25, 0));
    [523, 659, 784, 1047, 1319].forEach((f, i) =>
        playTone(f, 0.3, 'triangle', 0.3, 0.5 + i * 0.1)
    );
};

/**
 * speakText
 * Web Speech API — speaks text aloud through the browser.
 * No API key or library required. Falls back silently.
 */
const speakText = (text, rate = 0.9) => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const utter  = new SpeechSynthesisUtterance(text);
    utter.rate   = rate;
    utter.pitch  = 1.1;
    utter.volume = 1;
    window.speechSynthesis.speak(utter);
};

// ============================================================
//  STYLED COMPONENTS
//  MUI styled wrappers — identical to JavaQuiz for brand consistency.
// ============================================================

/** pulseGold — glowing pulse used on Begin button & Golden Buzzer */
const pulseGold = keyframes`
    0%, 100% { box-shadow: 0 0 0 0 rgba(229,201,95,0.4); }
    50%       { box-shadow: 0 0 0 20px rgba(229,201,95,0); }
`;

/** shakeAnim — horizontal shake for wrong answers & violations */
const shakeAnim = keyframes`
    0%, 100% { transform: translateX(0); }
    20%       { transform: translateX(-8px); }
    40%       { transform: translateX(8px); }
    60%       { transform: translateX(-6px); }
    80%       { transform: translateX(6px); }
`;

const PageWrapper = styled(Box)({
    minHeight:       '100vh',
    backgroundColor: BRAND.dark,
    fontFamily:      "'Poppins', sans-serif",
    padding:         '24px 16px 48px',
    overflowX:       'hidden',
});

const QuizCard = styled(Card)({
    backgroundColor: BRAND.darkCard,
    border:          `1px solid ${BRAND.darkBorder}`,
    borderRadius:    '20px',
    backdropFilter:  'blur(10px)',
    maxWidth:        '860px',
    margin:          '0 auto',
    overflow:        'visible',
});

const GoldButton = styled(Button)({
    background:    `linear-gradient(135deg, ${BRAND.gold}, ${BRAND.goldenrod})`,
    color:         BRAND.dark,
    fontFamily:    "'Poppins', sans-serif",
    fontWeight:    800,
    fontSize:      '0.9rem',
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    padding:       '12px 28px',
    borderRadius:  '10px',
    transition:    'transform 0.2s, box-shadow 0.2s',
    '&:hover': {
        background: `linear-gradient(135deg, ${BRAND.goldenrod}, ${BRAND.gold})`,
        transform:  'translateY(-2px)',
        boxShadow:  '0 8px 20px rgba(229,201,95,0.3)',
    },
    '&.Mui-disabled': {
        background: 'rgba(255,255,255,0.1)',
        color:      'rgba(255,255,255,0.3)',
    },
});

const OutlineButton = styled(Button)({
    borderColor:   BRAND.gold,
    color:         BRAND.gold,
    fontFamily:    "'Poppins', sans-serif",
    fontWeight:    700,
    borderRadius:  '10px',
    padding:       '10px 24px',
    transition:    'background 0.2s',
    '&:hover': {
        backgroundColor: BRAND.goldFaint,
        borderColor:     BRAND.goldenrod,
    },
});

const ViolationBanner = styled(Box)({
    backgroundColor: 'rgba(255,107,107,0.15)',
    border:          '1px solid rgba(255,107,107,0.4)',
    borderRadius:    '10px',
    padding:         '10px 16px',
    display:         'flex',
    alignItems:      'center',
    gap:             '10px',
    marginBottom:    '16px',
    animation:       `${shakeAnim} 0.4s ease`,
});

// ============================================================
//  STAR DISPLAY COMPONENT
//  Renders filled/outline stars. Used on results + leaderboard.
// ============================================================
const StarDisplay = ({ stars, size = 'large' }) => {
    const fontSize = size === 'large' ? '2.5rem' : '1.2rem';
    return (
        <Box display="flex" gap={0.5} justifyContent="center">
            {[1, 2, 3].map((i) =>
                i <= stars
                    ? <StarIcon key={i} sx={{ fontSize, color: BRAND.gold, filter: 'drop-shadow(0 0 4px rgba(229,201,95,0.6))' }} />
                    : <StarBorderIcon key={i} sx={{ fontSize, color: 'rgba(255,255,255,0.2)' }} />
            )}
        </Box>
    );
};

// ============================================================
//  MAIN COMPONENT — PythonQuiz
//
//  Add to App.js:
//    import PythonQuiz from "./pages/Languages/Python/PythonQuiz";
//    <Route path="/python-quiz" element={<PythonQuiz />} />
// ============================================================
function PythonQuiz() {

    // ── Phase & Identity ──────────────────────────────────────
    // 4 possible phases: 'welcome' → 'rules' → 'quiz' → results (finished)
    const [phase,      setPhase]     = useState('welcome');
    const [userName,   setUserName]  = useState('');
    const [nameInput,  setNameInput] = useState('');

    // ── Quiz State ────────────────────────────────────────────
    const [questions,   setQuestions]   = useState([]);
    const [current,     setCurrent]     = useState(0);
    const [score,       setScore]       = useState(0);
    const [selected,    setSelected]    = useState(null);
    const [showAnswer,  setShowAnswer]  = useState(false);
    const [userAnswers, setUserAnswers] = useState([]);
    const [isRetry,     setIsRetry]     = useState(false);

    // Stores accuracy per category from the last attempt.
    // Passed to reorderByDifficulty() on retry.
    const [prevCategoryScores, setPrevCategoryScores] = useState({});

    // ── Timer ─────────────────────────────────────────────────
    const [timer,    setTimer]    = useState(QUIZ_CONFIG.totalTimeSeconds);
    const [finished, setFinished] = useState(false);

    // ── Anti-Cheat ────────────────────────────────────────────
    const [violations,    setViolations]    = useState(0);
    const [violationLog,  setViolationLog]  = useState([]);
    const [showViolation, setShowViolation] = useState(false);
    const [lastViolation, setLastViolation] = useState('');

    // ── Refs ──────────────────────────────────────────────────
    // Junior dev note: useRef gives us a mutable value that does
    // NOT cause a re-render when changed. We use refs for values
    // that need to be read inside event listeners — event handler
    // closures would otherwise capture stale state values.
    const violationCountRef = useRef(0);
    const quizActiveRef     = useRef(false);
    const warningPlayedRef  = useRef(false);

    // ============================================================
    //  QUESTION INITIALISATION
    // ============================================================
    const initQuestions = useCallback((retry, catScores) => {
        let bank = pythonQuestionBank.map(q => ({ ...q, options: shuffle(q.options) }));
        if (retry && Object.keys(catScores).length > 0) {
            bank = reorderByDifficulty(bank, catScores);
        } else {
            bank = shuffle(bank);
        }
        setQuestions(bank);
    }, []);

    // ============================================================
    //  ANTI-CHEAT ENGINE
    //  Attaches document/window event listeners during quiz phase.
    //  All cleaned up on phase change or unmount.
    // ============================================================
    const recordViolation = useCallback((message) => {
        if (!quizActiveRef.current) return;

        violationCountRef.current += 1;
        const count = violationCountRef.current;

        setViolations(count);
        setViolationLog(prev => [...prev, `[${new Date().toLocaleTimeString()}] ${message}`]);
        setLastViolation(message);
        setShowViolation(true);

        playTone(440, 0.3, 'square', 0.3, 0);
        speakText('Violation detected. ' + message);

        setTimeout(() => setShowViolation(false), 4000);

        if (count >= QUIZ_CONFIG.maxViolations) {
            speakText('Maximum violations reached. Quiz terminated.');
            quizActiveRef.current = false;
            setFinished(true);
        }
    }, []);

    useEffect(() => {
        if (phase !== 'quiz') return;

        const handleVisibility  = () => { if (document.hidden) recordViolation('Tab switch or window minimised detected.'); };
        const handleBlur        = ()  => recordViolation('Browser window lost focus.');
        const handleContextMenu = (e) => { e.preventDefault(); recordViolation('Right-click attempt blocked.'); };
        const handleKeyDown     = (e) => {
            const blocked =
                (e.ctrlKey && ['c', 'v', 'u', 's', 'a', 'p'].includes(e.key.toLowerCase())) ||
                e.key === 'F12' ||
                (e.ctrlKey && e.shiftKey && ['i', 'j', 'c'].includes(e.key.toLowerCase()));
            if (blocked) {
                e.preventDefault();
                recordViolation(`Blocked shortcut: ${e.ctrlKey ? 'Ctrl+' : ''}${e.shiftKey ? 'Shift+' : ''}${e.key}`);
            }
        };
        const handleResize = () => {
            if (window.outerHeight - window.innerHeight > 160 || window.outerWidth - window.innerWidth > 160) {
                recordViolation('Possible DevTools opened — browser size change detected.');
            }
        };

        document.addEventListener('visibilitychange', handleVisibility);
        window.addEventListener('blur',          handleBlur);
        document.addEventListener('contextmenu', handleContextMenu);
        document.addEventListener('keydown',     handleKeyDown);
        window.addEventListener('resize',        handleResize);

        quizActiveRef.current = true;

        return () => {
            document.removeEventListener('visibilitychange', handleVisibility);
            window.removeEventListener('blur',          handleBlur);
            document.removeEventListener('contextmenu', handleContextMenu);
            document.removeEventListener('keydown',     handleKeyDown);
            window.removeEventListener('resize',        handleResize);
            quizActiveRef.current = false;
        };
    }, [phase, recordViolation]);

    // ============================================================
    //  COUNTDOWN TIMER
    // ============================================================
    useEffect(() => {
        if (phase !== 'quiz' || finished) return;
        if (timer <= 0) {
            speakText('Time is up. Quiz finished.');
            setFinished(true);
            return;
        }
        if (timer <= QUIZ_CONFIG.warningThreshold && timer % 10 === 0) {
            playWarningBeep();
            if (timer === QUIZ_CONFIG.warningThreshold) {
                speakText(`${QUIZ_CONFIG.warningThreshold} seconds remaining.`);
            }
        }
        const id = setTimeout(() => setTimer(t => t - 1), 1000);
        return () => clearTimeout(id);
    }, [timer, finished, phase]);

    // ============================================================
    //  RESULTS TRIGGER
    // ============================================================
    useEffect(() => {
        if (!finished || questions.length === 0) return;

        quizActiveRef.current = false;
        const stars = getStars(score, questions.length);

        if (stars === 3) {
            playGoldenBuzzer();
            speakText(`Congratulations ${userName}! Perfect score! You earned the Golden Buzzer and three stars!`);
        } else {
            playComplete();
            speakText(`Well done ${userName}. You scored ${score} out of ${questions.length}. You earned ${stars} star${stars !== 1 ? 's' : ''}.`);
        }

        const catStats = {};
        questions.forEach((q, i) => {
            if (!catStats[q.category]) catStats[q.category] = { correct: 0, total: 0 };
            catStats[q.category].total++;
            if (userAnswers[i] === q.answer) catStats[q.category].correct++;
        });
        setPrevCategoryScores(catStats);

        saveScore({
            date:          new Date().toLocaleString(),
            score,
            total:         questions.length,
            timeRemaining: timer,
            badge:         getBadge(stars),
            stars,
            name:          userName,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [finished]);

    // ============================================================
    //  EVENT HANDLERS
    // ============================================================

    const handleNameSubmit = () => {
        const trimmed = nameInput.trim();
        if (!trimmed) return;
        setUserName(trimmed);
        speakText(`Welcome ${trimmed}. Please read the rules carefully before you begin.`);
        setPhase('rules');
    };

    const handleStartQuiz = () => {
        initQuestions(isRetry, prevCategoryScores);
        setCurrent(0);
        setScore(0);
        setSelected(null);
        setShowAnswer(false);
        setUserAnswers([]);
        setTimer(QUIZ_CONFIG.totalTimeSeconds);
        setFinished(false);
        setViolations(0);
        setViolationLog([]);
        violationCountRef.current = 0;
        warningPlayedRef.current  = false;
        setPhase('quiz');

        // Audible countdown before first question
        speakText(`Are you ready, ${userName}?`, 0.85);
        setTimeout(() => speakText('3', 1.2), 1800);
        setTimeout(() => speakText('2', 1.2), 2800);
        setTimeout(() => speakText('1', 1.2), 3800);
        setTimeout(() => speakText('Begin!', 1.0), 4800);
    };

    const handleAnswer = (opt) => {
        if (showAnswer) return;
        setSelected(opt);
        setUserAnswers(prev => [...prev, opt]);
        setShowAnswer(true);
        if (opt === questions[current].answer) {
            setScore(s => s + 1);
            playCorrect();
        } else {
            playWrong();
        }
    };

    const handleNext = () => {
        if (current + 1 < questions.length) {
            setCurrent(c => c + 1);
            setShowAnswer(false);
            setSelected(null);
        } else {
            setFinished(true);
        }
    };

    const handleRetry = () => {
        setIsRetry(true);
        setPhase('rules');
        speakText(`Let's try again, ${userName}. Questions are now ordered based on your previous performance.`);
    };

    // ============================================================
    //  DERIVED VALUES
    // ============================================================
    const categoryStats = questions.reduce((acc, q, i) => {
        if (!acc[q.category]) acc[q.category] = { correct: 0, total: 0 };
        acc[q.category].total++;
        if (finished && userAnswers[i] === q.answer) acc[q.category].correct++;
        return acc;
    }, {});

    const stars          = finished ? getStars(score, questions.length) : 0;
    const passed         = stars >= 1;
    const isGoldenBuzzer = stars === 3;
    const progressPct    = questions.length > 0 ? (current / questions.length) * 100 : 0;
    const timerWarning   = timer <= QUIZ_CONFIG.warningThreshold;

    // ============================================================
    //  RENDER — WELCOME SCREEN
    // ============================================================
    if (phase === 'welcome') {
        return (
            <PageWrapper>
                <QuizCard>
                    <CardContent sx={{ p: { xs: 3, md: 5 } }}>
                        <Box textAlign="center" mb={4}>
                            <Typography sx={{ fontSize: '3rem', lineHeight: 1 }}>🐍</Typography>
                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.4rem' }, color: BRAND.white, letterSpacing: '-0.02em', mt: 1 }}>
                                Python Mastery Quiz
                            </Typography>
                            <Typography sx={{ color: BRAND.dimWhite, mt: 0.5, fontFamily: "'Poppins', sans-serif", fontSize: '0.9rem' }}>
                                ProDev Adaptive Learning Platform
                            </Typography>
                            <Box mt={2}><StarDisplay stars={3} size="large" /></Box>
                        </Box>

                        <Divider sx={{ borderColor: BRAND.darkBorder, mb: 4 }} />

                        <Box maxWidth={420} mx="auto">
                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600, fontSize: '1rem', color: BRAND.white, mb: 0.5 }}>
                                Enter your full name to begin
                            </Typography>
                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '0.82rem', color: BRAND.dimWhite, mb: 2 }}>
                                Your name will appear on your certificate and leaderboard entry.
                            </Typography>

                            <Box
                                component="input"
                                placeholder="e.g. Ama Owusu"
                                value={nameInput}
                                onChange={(e) => setNameInput(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleNameSubmit()}
                                sx={{
                                    width: '100%', p: '14px 18px',
                                    borderRadius: '10px',
                                    border: `1px solid ${BRAND.darkBorder}`,
                                    backgroundColor: 'rgba(255,255,255,0.05)',
                                    color: BRAND.white,
                                    fontFamily: "'Poppins', sans-serif",
                                    fontSize: '1rem', outline: 'none', mb: 3,
                                    boxSizing: 'border-box',
                                    '&:focus':        { borderColor: BRAND.gold },
                                    '&::placeholder': { color: 'rgba(255,255,255,0.3)' },
                                }}
                            />

                            <GoldButton
                                fullWidth
                                onClick={handleNameSubmit}
                                disabled={!nameInput.trim()}
                                endIcon={<ArrowForwardIcon />}
                            >
                                Continue to Rules
                            </GoldButton>
                        </Box>
                    </CardContent>
                </QuizCard>
            </PageWrapper>
        );
    }

    // ============================================================
    //  RENDER — RULES SCREEN
    // ============================================================
    if (phase === 'rules') {
        const rules = [
            { icon: <TimerIcon />,               title: 'Time Limit',         text: `You have ${QUIZ_CONFIG.totalTimeSeconds / 60} minutes to answer all ${pythonQuestionBank.length} questions. The timer starts immediately when you click Begin.` },
            { icon: <StarIcon />,                 title: 'Star Rewards',       text: 'Score 80%+ for ★, 90%+ for ★★, and 100% for ★★★ (Golden Buzzer!). You need at least ★ to unlock the Final Certification Exam.' },
            { icon: <ReplayIcon />,               title: 'Failed Attempts',    text: 'Scoring below 80% means you must retake. Questions will be reordered — easiest topics first, based on your weakest categories.' },
            { icon: <SecurityIcon />,             title: 'Anti-Cheat Rules',   text: `Do NOT switch tabs, minimise the browser, open DevTools, right-click, or use Ctrl+C/V. ${QUIZ_CONFIG.maxViolations} violations will auto-terminate your quiz.` },
            { icon: <NotificationsActiveIcon />,  title: 'Audio Enabled',      text: 'This quiz uses audio feedback and speech synthesis. Ensure your volume is on. You will hear a countdown before the quiz starts.' },
            { icon: <SchoolIcon />,               title: 'Academic Integrity', text: 'Do not search for answers online. This quiz tests YOUR Python knowledge. Integrity is what makes your certificate meaningful.' },
        ];

        return (
            <PageWrapper>
                <QuizCard>
                    <CardContent sx={{ p: { xs: 3, md: 5 } }}>
                        <Box textAlign="center" mb={3}>
                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 800, fontSize: { xs: '1.6rem', md: '2rem' }, color: BRAND.gold }}>
                                Welcome, {userName}! 👋
                            </Typography>
                            <Typography sx={{ color: BRAND.dimWhite, mt: 0.5, fontFamily: "'Poppins', sans-serif", fontSize: '0.88rem' }}>
                                {isRetry
                                    ? 'Retaking with adaptive difficulty — questions ordered by your weakest categories.'
                                    : 'Please read the rules carefully before you begin.'}
                            </Typography>
                        </Box>

                        <Divider sx={{ borderColor: BRAND.darkBorder, mb: 3 }} />

                        {rules.map((rule, i) => (
                            <Box key={i} display="flex" gap={2} alignItems="flex-start" mb={2}
                                 sx={{ p: 2, backgroundColor: BRAND.goldFaint, border: `1px solid ${BRAND.darkBorder}`, borderRadius: '12px' }}
                            >
                                <Box sx={{ color: BRAND.gold, mt: 0.2, flexShrink: 0 }}>{rule.icon}</Box>
                                <Box>
                                    <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: '0.9rem', color: BRAND.white, mb: 0.3 }}>
                                        {rule.title}
                                    </Typography>
                                    <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '0.82rem', color: BRAND.dimWhite, lineHeight: 1.6 }}>
                                        {rule.text}
                                    </Typography>
                                </Box>
                            </Box>
                        ))}

                        <Divider sx={{ borderColor: BRAND.darkBorder, my: 3 }} />

                        <Box textAlign="center">
                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: '1rem', color: BRAND.white, mb: 3 }}>
                                Are you ready, {userName}? You will hear a countdown before the first question.
                            </Typography>
                            <GoldButton
                                onClick={handleStartQuiz}
                                startIcon={<PlayArrowIcon />}
                                sx={{ fontSize: '1rem', px: 5, py: 1.5, animation: `${pulseGold} 2s ease-in-out infinite` }}
                            >
                                I'm Ready — Begin!
                            </GoldButton>
                        </Box>
                    </CardContent>
                </QuizCard>
            </PageWrapper>
        );
    }

    // ============================================================
    //  RENDER — RESULTS SCREEN
    // ============================================================
    if (finished) {
        return (
            <PageWrapper>
                <QuizCard>
                    <CardContent sx={{ p: { xs: 3, md: 5 } }}>

                        {/* Golden Buzzer — 100% only */}
                        {isGoldenBuzzer && (
                            <Box textAlign="center" sx={{
                                background: 'linear-gradient(135deg, rgba(229,201,95,0.2), rgba(218,165,32,0.1))',
                                border: `2px solid ${BRAND.gold}`, borderRadius: '16px',
                                p: 3, mb: 3,
                                animation: `${pulseGold} 1.5s ease-in-out infinite`,
                            }}>
                                <Typography sx={{ fontSize: '2.5rem' }}>🔔</Typography>
                                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 800, fontSize: '1.5rem', color: BRAND.gold }}>
                                    GOLDEN BUZZER!
                                </Typography>
                                <Typography sx={{ color: BRAND.dimWhite, fontFamily: "'Poppins', sans-serif", fontSize: '0.9rem' }}>
                                    Perfect score! Outstanding Python mastery, {userName}!
                                </Typography>
                            </Box>
                        )}

                        {/* Score + Stars */}
                        <Box textAlign="center" mb={3}>
                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.2rem' }, color: BRAND.white, mb: 1 }}>
                                {passed ? '🎉 Well Done,' : '😓 Keep Going,'} {userName}!
                            </Typography>
                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '1.1rem', color: BRAND.dimWhite, mb: 2 }}>
                                Score:{' '}
                                <Box component="span" sx={{ color: BRAND.gold, fontWeight: 700 }}>{score}</Box>
                                {' '}/ {questions.length} ({Math.round((score / questions.length) * 100)}%)
                            </Typography>
                            <StarDisplay stars={stars} size="large" />
                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '1rem', color: BRAND.gold, mt: 1.5, fontWeight: 600 }}>
                                {getBadge(stars)}
                            </Typography>
                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '0.82rem', color: BRAND.dimWhite, mt: 0.5 }}>
                                ⏱ Time remaining: {formatTime(timer)}
                            </Typography>
                        </Box>

                        {/* Certification gate */}
                        {passed ? (
                            <Box sx={{ p: 2.5, mb: 3, backgroundColor: 'rgba(62,207,142,0.08)', border: '1px solid rgba(62,207,142,0.3)', borderRadius: '12px', textAlign: 'center' }}>
                                <EmojiEventsIcon sx={{ color: BRAND.success, fontSize: 32, mb: 0.5 }} />
                                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, color: BRAND.success, fontSize: '0.95rem', mb: 2 }}>
                                    You are eligible for the Python Certification Exam!
                                </Typography>
                                <GoldButton component={Link} to="/python-certification-exam" endIcon={<ArrowForwardIcon />}>
                                    Attempt Certification Exam
                                </GoldButton>
                            </Box>
                        ) : (
                            <Box sx={{ p: 2.5, mb: 3, backgroundColor: 'rgba(255,107,107,0.08)', border: '1px solid rgba(255,107,107,0.3)', borderRadius: '12px', textAlign: 'center' }}>
                                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, color: BRAND.error, fontSize: '0.95rem', mb: 0.5 }}>
                                    You need 80%+ to unlock the Python Certification Exam.
                                </Typography>
                                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '0.82rem', color: BRAND.dimWhite, mb: 2 }}>
                                    The retake reorders questions — easier topics first based on your weak areas.
                                </Typography>
                                <OutlineButton onClick={handleRetry} startIcon={<ReplayIcon />} variant="outlined">
                                    Retake Quiz (Adaptive)
                                </OutlineButton>
                            </Box>
                        )}

                        {/* Violation log */}
                        {violations > 0 && (
                            <Box sx={{ mb: 3, p: 2, backgroundColor: 'rgba(255,107,107,0.06)', border: '1px solid rgba(255,107,107,0.25)', borderRadius: '12px' }}>
                                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, color: BRAND.error, fontSize: '0.88rem', mb: 1 }}>
                                    ⚠ {violations} Violation{violations > 1 ? 's' : ''} Detected
                                </Typography>
                                {violationLog.map((v, i) => (
                                    <Typography key={i} sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '0.78rem', color: 'rgba(255,107,107,0.7)', lineHeight: 1.7 }}>
                                        {v}
                                    </Typography>
                                ))}
                            </Box>
                        )}

                        {/* Category breakdown */}
                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, color: BRAND.white, mb: 1.5, fontSize: '1rem' }}>
                            Category Breakdown
                        </Typography>
                        <Box display="flex" flexDirection="column" gap={1} mb={3}>
                            {Object.entries(categoryStats).map(([cat, data]) => {
                                const pct      = Math.round((data.correct / data.total) * 100);
                                const barColor = pct >= 80 ? BRAND.success : pct >= 50 ? BRAND.warning : BRAND.error;
                                return (
                                    <Box key={cat} sx={{ p: '10px 16px', backgroundColor: BRAND.goldFaint, border: `1px solid ${BRAND.darkBorder}`, borderRadius: '10px' }}>
                                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600, fontSize: '0.82rem', color: BRAND.white }}>
                                                {cat}
                                            </Typography>
                                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '0.78rem', color: barColor, fontWeight: 700 }}>
                                                {data.correct}/{data.total} ({pct}%)
                                            </Typography>
                                        </Box>
                                        <LinearProgress
                                            variant="determinate"
                                            value={pct}
                                            sx={{
                                                height: 4, borderRadius: 2,
                                                backgroundColor: 'rgba(255,255,255,0.08)',
                                                '& .MuiLinearProgress-bar': { background: barColor, borderRadius: 2 },
                                            }}
                                        />
                                    </Box>
                                );
                            })}
                        </Box>

                        {/* Leaderboard */}
                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, color: BRAND.white, mb: 1.5, fontSize: '1rem' }}>
                            🏅 Leaderboard — Top 5
                        </Typography>
                        <Box display="flex" flexDirection="column" gap={1} mb={3}>
                            {loadScores().map((entry, idx) => (
                                <Box key={idx} sx={{
                                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                    p: '10px 16px',
                                    backgroundColor: idx === 0 ? 'rgba(229,201,95,0.1)' : BRAND.goldFaint,
                                    border: `1px solid ${idx === 0 ? BRAND.gold : BRAND.darkBorder}`,
                                    borderRadius: '10px',
                                }}>
                                    <Box>
                                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: '0.85rem', color: idx === 0 ? BRAND.gold : BRAND.white }}>
                                            #{idx + 1} {entry.name}
                                        </Typography>
                                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '0.72rem', color: BRAND.dimWhite }}>
                                            {entry.date} • ⏱ {formatTime(entry.timeRemaining)} left
                                        </Typography>
                                    </Box>
                                    <Box textAlign="right">
                                        <StarDisplay stars={entry.stars} size="small" />
                                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '0.72rem', color: BRAND.dimWhite }}>
                                            {entry.score}/{entry.total}
                                        </Typography>
                                    </Box>
                                </Box>
                            ))}
                        </Box>

                        {/* Navigation */}
                        <Box display="flex" gap={2} flexWrap="wrap">
                            <OutlineButton component={Link} to="/home" variant="outlined">
                                Back to Home
                            </OutlineButton>
                            {passed && (
                                <OutlineButton onClick={handleRetry} startIcon={<ReplayIcon />} variant="outlined">
                                    Retake for Better Score
                                </OutlineButton>
                            )}
                        </Box>
                    </CardContent>
                </QuizCard>
            </PageWrapper>
        );
    }

    // ============================================================
    //  RENDER — ACTIVE QUIZ SCREEN
    // ============================================================
    if (phase === 'quiz' && questions.length > 0) {
        const q = questions[current];

        return (
            <PageWrapper>

                {/* ── Sticky progress header ──────────────────── */}
                <Box sx={{
                    position: 'sticky', top: 0, zIndex: 10,
                    backgroundColor: BRAND.dark,
                    borderBottom: `1px solid ${BRAND.darkBorder}`,
                    py: 1.5, px: 2, mb: 2,
                }}>
                    <Box maxWidth="860px" mx="auto"
                         display="flex" justifyContent="space-between"
                         alignItems="center" flexWrap="wrap" gap={1}
                    >
                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: '0.88rem', color: BRAND.dimWhite }}>
                            Question{' '}
                            <Box component="span" sx={{ color: BRAND.gold }}>{current + 1}</Box>
                            {' '}of {questions.length}
                        </Typography>

                        <Chip label={q.category} size="small" sx={{
                            backgroundColor: BRAND.goldFaint, color: BRAND.gold,
                            border: `1px solid ${BRAND.darkBorder}`,
                            fontFamily: "'Poppins', sans-serif", fontWeight: 600, fontSize: '0.7rem',
                        }} />

                        {/* Timer — red + pulsing in last 60 seconds */}
                        <Box display="flex" alignItems="center" gap={0.5}>
                            <TimerIcon sx={{ color: timerWarning ? BRAND.error : BRAND.gold, fontSize: '1.1rem' }} />
                            <Typography sx={{
                                fontFamily: "'Poppins', sans-serif", fontWeight: 800, fontSize: '1rem',
                                color: timerWarning ? BRAND.error : BRAND.gold,
                                animation: timerWarning ? `${pulseGold} 1s ease-in-out infinite` : 'none',
                            }}>
                                {formatTime(timer)}
                            </Typography>
                        </Box>

                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: '0.85rem', color: BRAND.dimWhite }}>
                            Score: <Box component="span" sx={{ color: BRAND.gold }}>{score}</Box>
                        </Typography>
                    </Box>

                    {/* Gold progress bar */}
                    <Box maxWidth="860px" mx="auto" mt={1}>
                        <LinearProgress
                            variant="determinate"
                            value={progressPct}
                            sx={{
                                height: 4, borderRadius: 2,
                                backgroundColor: 'rgba(255,255,255,0.06)',
                                '& .MuiLinearProgress-bar': {
                                    background: `linear-gradient(90deg, ${BRAND.gold}, ${BRAND.goldenrod})`,
                                    borderRadius: 2,
                                },
                            }}
                        />
                    </Box>
                </Box>

                {/* ── Anti-cheat violation banner ─────────────── */}
                {showViolation && (
                    <Box maxWidth="860px" mx="auto" px={2} mb={1}>
                        <ViolationBanner>
                            <WarningAmberIcon sx={{ color: BRAND.error, flexShrink: 0 }} />
                            <Box>
                                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: '0.85rem', color: BRAND.error }}>
                                    ⚠ Violation #{violations} Detected
                                </Typography>
                                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '0.78rem', color: 'rgba(255,107,107,0.8)' }}>
                                    {lastViolation} — {QUIZ_CONFIG.maxViolations - violations} more will terminate this quiz.
                                </Typography>
                            </Box>
                        </ViolationBanner>
                    </Box>
                )}

                {/* ── Question card ───────────────────────────── */}
                <QuizCard>
                    <CardContent sx={{ p: { xs: 2.5, md: 4 } }}>

                        {/* Question text */}
                        <Typography sx={{
                            fontFamily: "'Poppins', sans-serif", fontWeight: 700,
                            fontSize: { xs: '1rem', md: '1.15rem' },
                            color: BRAND.white, mb: 3, lineHeight: 1.6,
                            p: 2.5,
                            backgroundColor: BRAND.goldFaint,
                            borderLeft: `3px solid ${BRAND.gold}`,
                            borderRadius: '0 10px 10px 0',
                        }}>
                            {current + 1}. {q.question}
                        </Typography>

                        {/* Answer options */}
                        <Box>
                            {q.options.map((opt, i) => {
                                const isCorrect = showAnswer && opt === q.answer;
                                const isWrong   = showAnswer && opt === selected && opt !== q.answer;
                                const bgColor   = isCorrect ? 'rgba(62,207,142,0.15)'  : isWrong ? 'rgba(255,107,107,0.15)' : 'rgba(255,255,255,0.04)';
                                const borderClr = isCorrect ? BRAND.success            : isWrong ? BRAND.error              : 'rgba(255,255,255,0.12)';
                                const textColor = isCorrect ? BRAND.success            : isWrong ? BRAND.error              : BRAND.white;

                                return (
                                    <Button
                                        key={i}
                                        onClick={() => handleAnswer(opt)}
                                        disabled={showAnswer}
                                        startIcon={
                                            isCorrect ? <CheckCircleIcon sx={{ color: BRAND.success }} /> :
                                                isWrong   ? <CancelIcon      sx={{ color: BRAND.error   }} /> :
                                                    null
                                        }
                                        sx={{
                                            width: '100%', textAlign: 'left',
                                            justifyContent: 'flex-start',
                                            p: '14px 20px', borderRadius: '10px',
                                            fontFamily: "'Poppins', sans-serif",
                                            fontWeight: 500, fontSize: '0.9rem',
                                            textTransform: 'none', mb: 1,
                                            backgroundColor: bgColor,
                                            border: `1px solid ${borderClr}`,
                                            color: textColor,
                                            transition: 'all 0.25s ease',
                                            animation: isWrong ? `${shakeAnim} 0.4s ease` : 'none',
                                            '&:hover': !showAnswer ? {
                                                backgroundColor: 'rgba(229,201,95,0.08)',
                                                borderColor: BRAND.gold,
                                            } : {},
                                            '&.Mui-disabled': { opacity: 1 },
                                        }}
                                    >
                                        {opt}
                                    </Button>
                                );
                            })}
                        </Box>

                        {/* Explanation — shown after answering */}
                        {showAnswer && (
                            <Box sx={{
                                mt: 2, p: 2,
                                backgroundColor: selected === q.answer ? 'rgba(62,207,142,0.06)' : 'rgba(255,107,107,0.06)',
                                border: `1px solid ${selected === q.answer ? 'rgba(62,207,142,0.3)' : 'rgba(255,107,107,0.3)'}`,
                                borderRadius: '10px',
                            }}>
                                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600, fontSize: '0.85rem', color: selected === q.answer ? BRAND.success : BRAND.error, mb: 0.5 }}>
                                    {selected === q.answer ? '✅ Correct!' : `❌ Incorrect — Correct answer: ${q.answer}`}
                                </Typography>
                                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '0.82rem', color: BRAND.dimWhite, lineHeight: 1.6, fontStyle: 'italic' }}>
                                    {q.explanation}
                                </Typography>
                                <Typography
                                    component={Link}
                                    to={q.link}
                                    sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '0.78rem', color: BRAND.gold, mt: 0.5, display: 'block', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                                >
                                    📖 Review this topic →
                                </Typography>
                            </Box>
                        )}

                        {/* Next / Finish */}
                        {showAnswer && (
                            <Box mt={3} display="flex" justifyContent="flex-end">
                                <GoldButton onClick={handleNext} endIcon={<ArrowForwardIcon />}>
                                    {current + 1 < questions.length ? 'Next Question' : 'Finish Quiz'}
                                </GoldButton>
                            </Box>
                        )}
                    </CardContent>
                </QuizCard>
            </PageWrapper>
        );
    }

    // Fallback — should never appear in normal flow
    return (
        <PageWrapper>
            <Typography sx={{ color: BRAND.white, textAlign: 'center', pt: 10, fontFamily: "'Poppins', sans-serif" }}>
                Loading quiz...
            </Typography>
        </PageWrapper>
    );
}

export default PythonQuiz;
